import { Children, memo, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { dialog, json, storage } from 'app/utils';
import { useLoader, useMountEffect } from 'app/hooks';
import { Breadcrumb } from 'app/components';
import { Button } from 'app/components/common';
import { Table, ITable, IPaging } from 'app/components/data';
import { TextBox } from 'app/components/form';
import { templates } from 'app/components/utils';
import { users } from 'app/services';
import { IUser } from 'app/models';

const stateKey = 'modules.users';
const loader = (params: IPaging) => {
  storage.set(stateKey, params);
  const { page, rows, sort, filters } = params;

  return users.query({ sort: sort?.field, order: sort?.order, search: filters?.global?.value, skip: (page * rows), take: rows });
};

export default memo(() => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const statuses: Record<number, string> = useMemo(() => ({
    0: t('app.status.disabled'),
    1: t('app.status.enabled'),
    2: t('app.status.approved'),
  }), [t]);

  const { loading, data, total, params, reload } = useLoader(loader);
  const [search, setSearch] = useState<string>();

  const actions: Record<string, (item: IUser) => Promise<any> | void> = useMemo(() => ({
    delete: (item) => dialog.confirm({
      message: t('users.delete.question', { ...item }),
      accept: { label: t('app.delete'), className: 'ml-2 p-button-danger' },
      reject: { label: t('app.cancel') },
    }).then((accepted) => {
      if (accepted) users.delete(item).then(({ success }) => {
        if (success) dialog.toast({ severity: 'success', message: t(`users.delete.success`, { ...item }) });

        return reload(params);
      });
    }),
  }), [params, reload, t]);

  const table: ITable<IUser> = { data, total, loading,
    className: ['min-h-0', 'flex-auto'],
    columns: [
      { field: 'username', type: 'string', header: t('users.fields.username'), sortable: true },
      { field: 'roleId', type: 'string', header: { label: t('users.fields.role'), className: 'w-12rem' },
        template: (item) => t(`app.role.${item.role?.name}`),
      },
      { field: 'profile', type: 'string', header: { label: t('users.fields.profile'), className: 'w-20rem' }, className: 'p-0',
        template: ({ profile }) => templates.profile(profile),
      },
      { field: 'stamp', type: 'string', header: { label: t('users.fields.stamp'), className: 'w-20rem' }, className: 'p-0',
        template: ({ profile }) => templates.membership(profile?.stamp),
      },
      { field: 'country', type: 'string', header: { label: t('users.fields.country'), className: 'w-20rem' }, className: 'p-0',
        template: ({ profile }) => templates.country(profile?.country),
      },
      { field: 'nationality', type: 'string', header: { label: t('users.fields.nationality'), className: 'w-20rem' }, className: 'p-0',
        template: ({ profile }) => templates.country(profile?.nationality),
      },
      { field: 'status', type: 'boolean', header: { label: t('users.fields.status'), className: 'w-3rem' },
        template: (item) => statuses[item.status],
      },
      { field: 'actions', align: 'right', header: { className: 'w-6rem' }, className: 'p-0',
        template: (item) => Children.toArray([
          <Button href={`${item.id}`} text rounded icon="mdi-edit" />,
          <Button severity="danger" text rounded icon="mdi-delete" onClick={() => actions['delete'](item)} />,
        ]),
      },
    ],
    dataKey: 'id',
    removableSort: true,
    resizableColumns: true,
    scrollable: true,
    scrollHeight: 'flex',
    selectionMode: 'single',
    paginator: { show: true, page: params?.page, rows: params?.rows, options: [20, 50, 100], className: 'border-none' },
    sort: params?.sort,
    filters: params?.filters ?? {
      global: { value: undefined, matchMode: 'contains' }
    },

    onLoad: reload,
    onRowDoubleClick: (item) => navigate(item.id),
  };

  useMountEffect(() => {
    const params = (storage.get(stateKey) ?? { page: 0, rows: 20 }) as IPaging;

    setSearch(params.filters?.global?.value);
    reload(params).then();
  });

  return (
    <div className="h-full flex flex-column">
      <div className="flex flex-none surface-200">
        <Breadcrumb className="flex-auto border-none border-noround bg-transparent" />
        <div className="flex flex-none m-2">
          <Button text icon="mdi-cached" severity="success" className="mr-2 px-3 py-1" disabled={loading} onClick={() => reload(params)} />
          <TextBox placeholder="Search" clearable className="w-20rem -my-1 mr-2 py-2" value={search} onChange={({ target }) => setSearch((target as any).value || undefined)} onSubmit={() => {
            reload(json.build([{ path: 'filters.global.value', value: search }], params)).then();
          }} />
          <Button href="new" icon="mdi-add" label={t('users.new')} className="px-3 py-1" />
        </div>
      </div>
      <Table { ...table } />
    </div>
  );
});
